import { useCallback, useMemo } from 'react';
import { InformationPaneLayout } from './components/InformationPaneLayout';
import { Formik } from 'formik';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import { Trans, useTranslation } from 'react-i18next';

import {
  availableLocales,
  useCurrentParticipant,
  useDeleteParticipantAccount,
  useMe,
  useUpdateAccount,
  useUpdateParticipantField,
} from '@app/api';
import {
  CenteredLoader,
  Button,
  CenteredModal,
  useToast,
  QuestionTooltip,
} from '@app/core';
import { Select, TimezoneSelect } from '@app/form';
import { Theme } from '@app/theme';
import { setLocale } from '@app/i18n';
import { useMatch, useNavigate } from 'react-router-dom';

const availableLocalesOptions = availableLocales.map((locale) => ({
  label: 'locales.' + locale,
  value: locale,
}));

const useStyles = createUseStyles((theme: Theme) => ({
  button: {
    marginTop: theme.marginBase * 2,
    width: '100%',
  },
  deleteIcon: {
    fontSize: theme.iconSize.regular,
    fontWeight: 400,
    marginRight: theme.marginBase,
  },
  message: {
    marginBottom: theme.marginBase * 4,
  },
  paragraph: {
    ...theme.fonts.body,
    color: theme.colors.inkSecondary,
    marginBottom: theme.marginBase,
  },
  tooltip: {
    marginTop: theme.marginBase,
  },
}));

export const Settings = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();
  const { data: me, isLoading: isMeLoading } = useMe();
  const { data: participant, isLoading } = useCurrentParticipant();

  const navigate = useNavigate();
  const match = useMatch('/profile/settings/delete');
  const deleteConfirmationOpened = !!match;

  const { mutateAsync: updateParticipantField } = useUpdateParticipantField(
    participant?.id,
  );
  const { mutateAsync: updateAccount } = useUpdateAccount(me?.id);
  const { mutate: deleteAccount, isLoading: deleting } =
    useDeleteParticipantAccount({
      onError: toast.exception,
    });

  const confirmDelete = useCallback(
    () => navigate('/profile/settings/delete'),
    [navigate],
  );
  const cancelDelete = useCallback(
    () => navigate('/profile/settings'),
    [navigate],
  );

  const initialValues = useMemo(
    () => ({
      timezone: participant?.timezone || me?.timezone,
      locale:
        me?.locale ||
        (participant?.locales.length ? participant.locales[0] : 'en'),
      inPanel: !!participant?.panelId,
    }),
    [me, participant],
  );

  const onSubmit = useCallback(
    async (values: typeof initialValues) => {
      if (initialValues.timezone !== values.timezone) {
        await updateParticipantField({
          fieldName: 'timezone',
          text: values.timezone,
        });
      }
      await updateAccount({
        locale: values.locale,
        timezone: values.timezone,
      });
      setLocale(values.locale);
    },
    [initialValues],
  );

  if (isLoading || isMeLoading) {
    return (
      <InformationPaneLayout
        icon="ri-settings-3-line"
        title={t('participantSpace.profile.settings')}
        buttonDisabled
        noForm
      >
        <CenteredLoader />
      </InformationPaneLayout>
    );
  }

  return (
    <>
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        <InformationPaneLayout
          icon="ri-settings-3-line"
          title={t('participantSpace.profile.settings')}
        >
          <Select
            name="locale"
            label={t('settings.language')}
            secondaryLabel={t('settings.languageDescription')}
            options={availableLocalesOptions}
            noMargin
          />
          <QuestionTooltip
            title={t('settings.languageTooltip.content')}
            className={classes.tooltip}
          >
            <>{t('settings.languageTooltip.caption')}</>
          </QuestionTooltip>
          <TimezoneSelect name="timezone" label={t('settings.timezone')} />
          <Button
            color="blue"
            variant="transparent"
            type="button"
            text={t('settings.cookies')}
            className={classNames(classes.button, 'cky-banner-element')}
          />
          <Button
            type="button"
            variant="transparent"
            color="red"
            className={classes.button}
            onClick={confirmDelete}
          >
            <i
              className={classNames(classes.deleteIcon, 'ri-delete-bin-line')}
            />
            {t('settings.deleteAccount')}
          </Button>
        </InformationPaneLayout>
      </Formik>
      <CenteredModal
        isOpen={deleteConfirmationOpened}
        onRequestClose={cancelDelete}
        title={t('settings.delete.title')}
        buttons={[
          <Button
            type="button"
            color="inkSecondary"
            variant="muted"
            onClick={cancelDelete}
            text={t('settings.delete.cancel')}
            disabled={deleting}
          />,
          <Button
            type="button"
            color="red"
            onClick={() => deleteAccount()}
            text={t('settings.delete.confirm')}
            isLoading={deleting}
          />,
        ]}
      >
        <div className={classes.message}>
          <Trans
            i18nKey="settings.delete.message"
            components={{
              p: <p className={classes.paragraph} />,
            }}
          />
        </div>
      </CenteredModal>
    </>
  );
};
